import * as React from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { urlImagemProduto } from '@wms/shared';
import { moeda, numero } from '@/lib/utils';

/**
 * Cartão de hover dos mapas do inventário — o mesmo desenho do cartão da vaga
 * no mapa da Transferência: endereço, e embaixo os SKUs com foto e quantidade.
 *
 * Fica em `position: fixed` num portal, para não ser cortado pelo
 * `overflow-x-auto` da tabela do rack.
 */
export interface LinhaCartao {
  produto: number;
  descricao: string | null;
  ean?: string | null | undefined;
  quantidade: number;
  /** Quantidade de antes, quando o cartão mostra uma mudança (antes → depois). */
  anterior?: number | undefined;
  /**
   * Quando presente, o cartão compara: `quantidade` é o que foi CONTADO e
   * isto é o que a foto ESPERAVA — "contou 5 · esperado 4 (+1)".
   */
  esperado?: number | undefined;
  /** R$ da diferença (bipado − esperado), quando o custo é conhecido. */
  valorDiferenca?: number | undefined;
  /** Recado do produto, embaixo do SKU (ex.: "provável troca de vaga…"). */
  nota?: string | undefined;
}

export interface EnderecoCartao {
  localizador: number;
  nome: string;
  /** `null` enquanto carrega. */
  linhas: LinhaCartao[] | null;
}

export interface PontoCartao {
  x: number;
  y: number;
}

/** Estado do hover: guarda o ponto do mouse e o que estiver sob ele. */
export function useHoverCartao<T>(): {
  hover: (PontoCartao & { alvo: T }) | null;
  props: (alvo: T) => {
    onMouseEnter: (e: React.MouseEvent) => void;
    onMouseMove: (e: React.MouseEvent) => void;
    onMouseLeave: () => void;
  };
} {
  const [hover, definirHover] = React.useState<(PontoCartao & { alvo: T }) | null>(null);
  const props = (alvo: T) => {
    const mostrar = (e: React.MouseEvent) => definirHover({ x: e.clientX, y: e.clientY, alvo });
    return { onMouseEnter: mostrar, onMouseMove: mostrar, onMouseLeave: () => definirHover(null) };
  };
  return { hover, props };
}

export function CartaoEndereco({
  ponto,
  titulo,
  etiqueta,
  rodape,
  aviso,
  enderecos,
}: {
  ponto: PontoCartao;
  /** Linha de cima, ex.: "Espera ser contado". */
  titulo: string;
  /** Selo à direita do título (estado do endereço). */
  etiqueta?: React.ReactNode;
  rodape?: React.ReactNode;
  /**
   * Recado em destaque logo abaixo do título — para o que o número sozinho
   * leria errado (ex.: "ainda não é divergência").
   */
  aviso?: { tom: 'info' | 'atencao'; texto: React.ReactNode } | undefined;
  enderecos: EnderecoCartao[];
}): JSX.Element {
  const largura = 288;
  const inverter = ponto.x > window.innerWidth - largura - 24;
  const subir = ponto.y > window.innerHeight * 0.55;
  return createPortal(
    <div
      role="status"
      className="pointer-events-none fixed z-[60] overflow-hidden rounded-xl text-xs leading-relaxed text-white shadow-forte ring-1 ring-white/10"
      style={{
        width: largura,
        background: 'linear-gradient(180deg, #1e2436 0%, #141824 100%)',
        left: inverter ? undefined : ponto.x + 16,
        right: inverter ? window.innerWidth - ponto.x + 16 : undefined,
        top: subir ? undefined : ponto.y + 16,
        bottom: subir ? window.innerHeight - ponto.y + 16 : undefined,
      }}
    >
      <div className="flex items-center gap-2 px-3 pb-1.5 pt-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-white/55">{titulo}</span>
        {etiqueta ? <span className="ml-auto">{etiqueta}</span> : null}
      </div>
      {aviso ? (
        <p
          className={`mx-3 mb-2 rounded-lg px-2.5 py-1.5 text-[11.5px] leading-snug ${
            aviso.tom === 'atencao'
              ? 'bg-amber-400/15 text-amber-100 ring-1 ring-inset ring-amber-300/30'
              : 'bg-sky-400/15 text-sky-100 ring-1 ring-inset ring-sky-300/30'
          }`}
        >
          {aviso.texto}
        </p>
      ) : null}
      <div className="max-h-[300px] overflow-hidden">
        {enderecos.map((end) => (
          <div key={end.localizador} className="border-t border-white/10">
            <p className="flex items-center gap-2 px-3 pt-2">
              <span className="font-mono text-[13px] font-bold tracking-wide text-indigo-200">{end.nome}</span>
              <span className="rounded bg-white/10 px-1 py-px font-mono text-[10px] font-bold text-white/75">
                LOC {end.localizador}
              </span>
            </p>
            {end.linhas === null ? (
              <p className="flex items-center gap-1.5 px-3 py-2 text-white/60">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> carregando…
              </p>
            ) : end.linhas.length === 0 ? (
              <p className="px-3 py-2 text-white/60">Sem saldo — nada a contar aqui.</p>
            ) : (
              end.linhas.map((p) => (
                <div key={p.produto} className="flex items-center gap-2 px-3 py-1.5">
                  <img
                    src={urlImagemProduto(p.produto)}
                    alt=""
                    loading="lazy"
                    width={40}
                    height={40}
                    className="h-10 w-10 shrink-0 rounded-md bg-white object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-medium text-white">{p.descricao ?? `Produto ${p.produto}`}</p>
                    <p className="text-[10.5px] text-white/55">
                      SKU {p.produto}
                      {p.ean ? ` · EAN ${p.ean}` : ''}
                    </p>
                    {p.nota ? <p className="text-[10.5px] font-semibold leading-snug text-amber-200">{p.nota}</p> : null}
                  </div>
                  <div className="shrink-0 text-right">
                    {/* Comparando, o número grande precisa dizer o que é: "Bipado". */}
                    {p.esperado !== undefined ? (
                      <span className="mr-1 text-[10.5px] font-semibold text-white/55">Bipado:</span>
                    ) : null}
                    <b className={`tabular text-[14px] ${p.quantidade < 0 ? 'text-red-300' : 'text-white'}`}>
                      {numero(p.quantidade)}
                    </b>
                    {p.esperado !== undefined ? (
                      <>
                        <span className="block text-[10px] font-semibold text-white/70">
                          Esperado: {numero(p.esperado)}
                        </span>
                        {/* Bipado − esperado, numa linha só dela: é o ajuste que o fechamento
                            faria. NÃO chamar de "saldo": com 4 esperados e 4 bipados o saldo
                            continua 4, e "saldo 0" lia como estoque zerado. */}
                        <span
                          className={`block text-[10px] font-bold ${
                            p.quantidade === p.esperado
                              ? 'text-emerald-300'
                              : p.quantidade < p.esperado
                                ? 'text-red-300'
                                : 'text-violet-300'
                          }`}
                        >
                          {p.quantidade === p.esperado
                            ? 'Diferença: nenhuma ✓'
                            : `Diferença: ${p.quantidade > p.esperado ? '+' : ''}${numero(p.quantidade - p.esperado)} un`}
                          {p.valorDiferenca !== undefined && p.quantidade !== p.esperado ? (
                            <span className="block font-semibold">{moeda(p.valorDiferenca)}</span>
                          ) : null}
                        </span>
                      </>
                    ) : p.anterior !== undefined && p.anterior !== p.quantidade ? (
                      <span
                        className={`block text-[10px] font-semibold ${p.quantidade > p.anterior ? 'text-emerald-300' : 'text-red-300'}`}
                      >
                        era {numero(p.anterior)} ({p.quantidade > p.anterior ? '+' : ''}
                        {numero(p.quantidade - p.anterior)})
                      </span>
                    ) : (
                      <span className="block text-[10px] text-white/55">un</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>
      {rodape ? <p className="border-t border-white/10 px-3 py-1.5 text-[10.5px] text-white/55">{rodape}</p> : null}
    </div>,
    document.body,
  );
}
